import { Box, Stack, useTheme } from "@mui/material";
import { Button, SearchBox, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { useGetNetworkFilterOptions } from "src/api/client-api/network/useNetworkLists";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import type { RemoteErrorStatus } from "src/api/remoteCollectionState";
import { useListExportAction } from "src/components/Grid/headers/useListExportAction";
import { GridTableActions, type TableAction } from "src/components/Grid/headers/GridTableActions";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import type { DelayedSearch } from "src/utils/useDelayedSearch";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import type { NetworkOption } from "./networkFilters";

export type NetworkTab = "organizations" | "users";

export const GRID_PANEL_ID = "network-grid-panel";

export const tabElementId = (tab: NetworkTab) => `network-tab-${tab}`;

export const toOptions = (options: { id: number; label: string }[]): NetworkOption[] =>
    options.map((option) => ({ value: String(option.id), label: option.label }));

export type NetworkListProps = { filterOptions: ReturnType<typeof useGetNetworkFilterOptions> };

export const tablePanelProps = (tab: NetworkTab) => ({
    id: GRID_PANEL_ID,
    role: "tabpanel",
    "aria-labelledby": tabElementId(tab),
});

export const NetworkToolbar = (props: { search: DelayedSearch; placeholder: string; actions: TableAction[] }) => {
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={theme.spacing(2)}
            sx={{ padding: theme.spacing(2, 2, 0) }}
        >
            <Box sx={{ maxWidth: "100%", width: theme.spacing(46) }}>
                <SearchBox
                    variant="standalone"
                    value={props.search.search}
                    placeholder={props.placeholder}
                    onChange={props.search.setSearch}
                />
            </Box>
            {!isMobile && (
                <Stack direction="row" gap={theme.spacing(1)}>
                    {props.actions.map((action) => (
                        <GridTableActions key={action.label} actions={[action]} appearance="themed" />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export const NetworkError = (props: { status: RemoteErrorStatus; onRetry: () => void }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Box sx={{ padding: theme.spacing(2) }}>
            <RemoteErrorAlert
                status={props.status}
                message={t("une_erreur_est_survenue", "An error occurred")}
                onRetry={props.onRetry}
                retryLabel={t("common.actions.refresh", "Refresh")}
            />
        </Box>
    );
};

export const NetworkEmptyState = (props: { hasCriteria: boolean; onReset: () => void }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack alignItems="center" justifyContent="center" gap={theme.spacing(2)} padding={theme.spacing(4)}>
            <Typography variant="body">
                {props.hasCriteria ?
                    t("network.empty.message", "No result matches these filters.")
                :   t("network.empty.none", "Nothing to show in this network yet.")}
            </Typography>
            {props.hasCriteria && (
                <Button variant="secondary" onClick={props.onReset}>
                    {t("network.empty.reset", "Reset filters")}
                </Button>
            )}
        </Stack>
    );
};

type NetworkExportOptions = {
    path: (organizationId: number) => string;
    fileNameStem: string;
    filters: AxiosParams;
    sort: AxiosParams;
    search: string;
    campaignId?: number;
    totalItems: number;
    entityLabel: string;
};

export const useNetworkExportAction = (options: NetworkExportOptions) => {
    const { path, campaignId, totalItems, entityLabel, ...exportParams } = options;
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();
    const organizationId = selectedCompany?.id ?? 0;

    return useListExportAction({
        ...exportParams,
        path: path(organizationId),
        campaignId,
        successMessage: t("network.export.success", "Export downloaded: {{total}} {{entity}}", {
            total: totalItems,
            entity: entityLabel,
        }),
        isDisabled: selectedCompany === null || totalItems === 0,
    });
};
